import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { useToast } from '../../contexts/ToastContext';
import { Upload, X, Save } from 'lucide-react';
import axios from 'axios';

const FaviconManager: React.FC = () => {
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [currentFavicon, setCurrentFavicon] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { success, error } = useToast();

  useEffect(() => {
    fetchCurrentFavicon();
  }, []);

  const fetchCurrentFavicon = async () => {
    try {
      const response = await axios.get('/api/admin/favicon-url');
      if (response.data.success) {
        setCurrentFavicon(response.data.faviconUrl || null);
      }
    } catch (err) {
      console.error('Failed to fetch current favicon:', err);
    }
  };

  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFaviconFile(file);
      const previewUrl = URL.createObjectURL(file);
      setFaviconPreview(previewUrl);
    }
  };

  const uploadFavicon = async () => {
    if (!faviconFile) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('favicon', faviconFile);
      
      const response = await axios.post('/api/upload/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      if (response.data.success) {
        success('Favicon uploaded successfully!');
        setFaviconFile(null);
        setFaviconPreview(null);
        await fetchCurrentFavicon();
        
        // Trigger favicon update in the browser
        const faviconUrl = response.data.data.accessUrl
          || (await getSignedFaviconUrl(response.data.data.key || response.data.data.faviconUrl));
        updateFaviconInBrowser(faviconUrl);
      }
    } catch (err: any) {
      console.error('Failed to upload favicon:', err);
      error(err.response?.data?.message || 'Failed to upload favicon');
    } finally {
      setLoading(false);
    }
  };

  const getSignedFaviconUrl = async (faviconKey: string) => {
    try {
      const response = await fetch(`/api/admin/sponsor-logo-url?key=${encodeURIComponent(faviconKey)}`);
      const data = await response.json();
      if (data.success) {
        return data.signedUrl;
      }
    } catch (error) {
      console.error('Failed to get signed favicon URL:', error);
    }
    return null;
  };

  const updateFaviconInBrowser = (faviconUrl: string | null) => {
    if (faviconUrl) {
      // Update all favicon links
      const faviconLinks = document.querySelectorAll('link[rel*="icon"]');
      faviconLinks.forEach(link => {
        link.setAttribute('href', faviconUrl);
      });
      
      // If no favicon links exist, create one
      if (faviconLinks.length === 0) {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = faviconUrl;
        document.head.appendChild(link);
      }
    }
  };

  const clearFavicon = () => {
    setFaviconFile(null);
    setFaviconPreview(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Manage Favicon</h3>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Current Favicon</label>
            {currentFavicon ? (
              <div className="flex items-center space-x-4">
                <img 
                  src={currentFavicon} 
                  alt="Current favicon" 
                  className="w-8 h-8 object-contain border rounded"
                />
                <span className="text-sm text-gray-600">Favicon is active</span>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No favicon uploaded</div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Upload New Favicon *</label>
            <Input
              type="file"
              accept=".ico,.png,.svg"
              onChange={handleFaviconChange}
              className="cursor-pointer"
            />
            <p className="text-xs text-gray-500 mt-1">
              Supported formats: ICO, PNG, SVG. Maximum size: 1MB
            </p>
          </div>

          {faviconPreview && (
            <div>
              <label className="block text-sm font-medium mb-2">Preview</label>
              <div className="flex items-center space-x-4">
                <img 
                  src={faviconPreview} 
                  alt="Favicon preview" 
                  className="w-8 h-8 object-contain border rounded"
                />
                <Button variant="ghost" size="sm" onClick={clearFavicon}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button 
              onClick={uploadFavicon} 
              disabled={!faviconFile || loading}
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              {loading ? 'Uploading...' : 'Upload Favicon'}
            </Button>
            {faviconFile && (
              <Button variant="outline" onClick={clearFavicon}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default FaviconManager; 